/**
 * IndexedDB storage for webenv state persistence.
 *
 * Database: gotts-webenv (version 1)
 * Stores:
 *   - deployments: deployment manifests + state dumps
 *   - snapshots: named snapshots with state data
 *   - settings: user preferences
 */

import type { Address, Hex } from "viem";

/** Snapshot metadata. */
export interface SnapshotEntry {
  name: string;
  description?: string;
  state: Hex;
  deployment: SnapshotDeployment;
  blockNumber: number;
  createdAt: number;
  sizeBytes: number;
}

/** Minimal deployment info stored with snapshots. */
export interface SnapshotDeployment {
  chainId: number;
  mode: string;
  addresses: Record<string, Address>;
}

/** Settings stored in IndexedDB. */
export interface WebenvSettings {
  theme: "light" | "dark" | "system";
  autoSaveInterval: number;
  autoSaveEnabled: boolean;
  defaultMode: string;
  webmcpEnabled: boolean;
  webmcpProfile: string;
}

const DB_NAME = "gotts-webenv";
const DB_VERSION = 1;
const DEPLOYMENT_STORE = "deployments";
const SNAPSHOT_STORE = "snapshots";
const SETTINGS_STORE = "settings";

/** Open or create the IndexedDB database. */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DEPLOYMENT_STORE)) {
        db.createObjectStore(DEPLOYMENT_STORE);
      }
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
        const store = db.createObjectStore(SNAPSHOT_STORE);
        store.createIndex("by-created", "createdAt");
      }
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
    };
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

/** Get or create the database connection. */
export function getDB(): Promise<IDBDatabase> {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing, eqeqeq
  if (dbPromise == null) {
    dbPromise = openDB();
  }
  return dbPromise;
}

/** Reset the DB connection (for testing). */
export function resetDB(): void {
  dbPromise = null;
}

// --- Snapshot operations ---

/** Save a named snapshot. */
export async function createSnapshot(entry: SnapshotEntry): Promise<void> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SNAPSHOT_STORE, "readwrite");
    tx.objectStore(SNAPSHOT_STORE).put(entry, entry.name);
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(new Error(`Snapshot save failed: ${tx.error?.message}`));
    };
  });
}

/** Load a snapshot by name. */
export async function getSnapshot(name: string): Promise<SnapshotEntry | null> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SNAPSHOT_STORE, "readonly");
    const request = tx.objectStore(SNAPSHOT_STORE).get(name);
    request.onsuccess = () => {
      resolve((request.result as SnapshotEntry | undefined) ?? null);
    };
    request.onerror = () => {
      reject(new Error(`Snapshot load failed: ${request.error?.message}`));
    };
  });
}

/** List all snapshots sorted by creation time (newest first). */
export async function listSnapshots(): Promise<SnapshotEntry[]> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SNAPSHOT_STORE, "readonly");
    const index = tx.objectStore(SNAPSHOT_STORE).index("by-created");
    const request = index.getAll();
    request.onsuccess = () => {
      const results = (request.result as SnapshotEntry[]).reverse();
      resolve(results);
    };
    request.onerror = () => {
      reject(new Error(`Snapshot list failed: ${request.error?.message}`));
    };
  });
}

/** Delete a snapshot by name. */
export async function deleteSnapshot(name: string): Promise<void> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SNAPSHOT_STORE, "readwrite");
    tx.objectStore(SNAPSHOT_STORE).delete(name);
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(new Error(`Snapshot delete failed: ${tx.error?.message}`));
    };
  });
}

// --- Settings operations ---

const DEFAULT_SETTINGS: WebenvSettings = {
  theme: "system",
  autoSaveInterval: 30_000,
  autoSaveEnabled: true,
  defaultMode: "from-scratch",
  webmcpEnabled: false,
  webmcpProfile: "webenv-full",
};

/** Get a setting value. */
export async function getSetting<K extends keyof WebenvSettings>(
  key: K,
): Promise<WebenvSettings[K]> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SETTINGS_STORE, "readonly");
    const request = tx.objectStore(SETTINGS_STORE).get(key);
    request.onsuccess = () => {
      const value = request.result as WebenvSettings[K] | undefined;
      resolve(value ?? DEFAULT_SETTINGS[key]);
    };
    request.onerror = () => {
      reject(new Error(`Setting read failed: ${request.error?.message}`));
    };
  });
}

/** Set a setting value. */
export async function setSetting<K extends keyof WebenvSettings>(
  key: K,
  value: WebenvSettings[K],
): Promise<void> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SETTINGS_STORE, "readwrite");
    tx.objectStore(SETTINGS_STORE).put(value, key);
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(new Error(`Setting write failed: ${tx.error?.message}`));
    };
  });
}

// --- Deployment cache (convenience wrappers around deployments store) ---

/** Save the latest deployment to cache. */
export async function cacheDeployment(
  key: string,
  data: unknown,
): Promise<void> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DEPLOYMENT_STORE, "readwrite");
    tx.objectStore(DEPLOYMENT_STORE).put(data, key);
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(new Error(`Deployment cache save failed: ${tx.error?.message}`));
    };
  });
}

/** Load a cached deployment. */
export async function loadCachedDeployment(key: string): Promise<unknown> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DEPLOYMENT_STORE, "readonly");
    const request = tx.objectStore(DEPLOYMENT_STORE).get(key);
    request.onsuccess = () => {
      resolve(request.result ?? null);
    };
    request.onerror = () => {
      reject(
        new Error(`Deployment cache load failed: ${request.error?.message}`),
      );
    };
  });
}
